import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import control from "../../../assets/control.png";
import Logo2 from "../../../assets/Edolutions 1.png";
import { MdSearch } from "react-icons/md";
import AiChatIcon from "../../../assets/new images/logo.png";
import {
  PiChalkboardTeacherFill,
  PiUserCircleGearFill,
  PiFilesFill,
} from "react-icons/pi";
import { SiGoogleclassroom } from "react-icons/si";
import { FaHome } from "react-icons/fa";

const SideNav = ({ isOpen, toggleSidebar }) => {
  const { t } = useTranslation();
  const handleNavClick = () => toggleSidebar();

  // base link styles
  const baseItem =
    "relative flex items-center gap-3 px-4 py-4 rounded-xl transition-colors";
  const baseIcon = "w-[22.5px] h-[22.5px]";
  const activeItem =
    "text-[#2A3A54] bg-white shadow-sm border border-black/10";
  const inactiveItem =
    "text-[#232738] hover:text-[#2A3A54] hover:bg-white/70";

  return (
    <>
      {/* Mobile overlay for when the drawer is open */}
      <div
        onClick={toggleSidebar}
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-30 xl:hidden transition-opacity ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        aria-label={t("studentDash.nav.sidebar")}
        className={`${
          isOpen ? "w-[240px]" : "w-0"
        } fixed xl:relative left-0 top-0 h-screen z-40 xl:z-10 bg-white/90 backdrop-blur border-r border-black/10 transition-all duration-300 overflow-hidden`}
      >
        {/* Collapse toggle (mobile only) */}
        <img
          src={control}
          alt={t("studentDash.nav.toggleSidebar")}
          className={`absolute block xl:hidden cursor-pointer -right-3 top-16 sm:top-16 md:top-5 w-7 border-2 border-black/10 rounded-full bg-white ${
            !isOpen && "rotate-180"
          }`}
          onClick={toggleSidebar}
        />

        {/* Brand / search section */}
        <div className="flex flex-col">
          {/* Logo row */}
          <div className="w-full p-4 md:hidden block">
            <img src={Logo2} alt="Edolutions" className="h-9 w-auto" />
          </div>

          {/* Gradient strip */}
          <div
            className={`${
              isOpen ? "opacity-100" : "opacity-0"
            } h-1 w-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] transition-opacity`}
          />

          {/* Mobile search */}
          <div className={`${isOpen ? "block" : "hidden"} px-4 py-3`}>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#232738]">
                <MdSearch className="sm:hidden block" />
              </span>
              <input
                type="text"
                placeholder={t("studentDash.nav.search")}
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-black/10 bg-white/80 shadow-sm outline-none sm:hidden block"
              />
            </div>
          </div>
        </div>

        {/* Nav items */}
        <nav className={`${isOpen ? "opacity-100" : "opacity-0"} transition-opacity`}>
          <ul className="mt-2 text-[14px] font-medium px-3 pb-6 space-y-1">
            <li>
              <NavLink
                to="./profile"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [
                    baseItem,
                    isActive ? activeItem : inactiveItem,
                    "group",
                  ].join(" ")
                }
              >
                {/* Active gradient bar */}
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]"
                  aria-hidden="true"
                />
                <FaHome className={`${baseIcon}`} />
                <span>{t("studentDash.nav.home")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./my-sessions"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [
                    baseItem,
                    isActive ? activeItem : inactiveItem,
                    "group",
                  ].join(" ")
                }
              >
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]"
                  aria-hidden="true"
                />
                <SiGoogleclassroom className={`${baseIcon}`} />
                <span>{t("studentDash.nav.mySessions")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./find-sessions"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [
                    baseItem,
                    isActive ? activeItem : inactiveItem,
                    "group",
                  ].join(" ")
                }
              >
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]"
                  aria-hidden="true"
                />
                <PiChalkboardTeacherFill className={`${baseIcon}`} />
                <span>{t("studentDash.nav.findMentor")}</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="./ai-chat"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [
                    baseItem,
                    isActive ? activeItem : inactiveItem,
                    "group",
                  ].join(" ")
                }
              >
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]"
                  aria-hidden="true"
                />
                <img src={AiChatIcon} className="w-4" alt={t("studentDash.nav.aiCareerCoach")} />
                <span>{t("studentDash.nav.aiCareerCoach")}</span>
              </NavLink>
            </li>

            {/* <li>
              <NavLink
                to="./menteeReport"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [
                    baseItem,
                    isActive ? activeItem : inactiveItem,
                    "group",
                  ].join(" ")
                }
              >
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]"
                  aria-hidden="true"
                />
                <PiFilesFill className={`${baseIcon}`} />
                <span>Reports</span>
              </NavLink>
            </li> */}

            <li>
              <NavLink
                to="./pro-setting"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [
                    baseItem,
                    isActive ? activeItem : inactiveItem,
                    "group",
                  ].join(" ")
                }
              >
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]"
                  aria-hidden="true"
                />
                <PiUserCircleGearFill className={`${baseIcon}`} />
                <span>{t("studentDash.nav.settings")}</span>
              </NavLink>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default SideNav;
